/**
 * Managed / supervised device detection.
 *
 * Native: a tiny Capacitor plugin "DevicePolicy" (source + install steps in
 * apps/client/native-plugins/device-policy/) returns {managed, supervised, purchasesAllowed}:
 *   iOS      managed app configuration present (MDM), SKPaymentQueue.canMakePayments()
 *   Android  DevicePolicyManager owner / work profile, UserManager DISALLOW_* restrictions
 * When the plugin is not installed the device counts as unmanaged. Web: no detection; the cheats menu
 * simulates the states (Settings → Developer / /cheats).
 */
import { registerPlugin } from '@capacitor/core';
import { deviceGate, UNMANAGED_DEVICE, type DeviceGate, type DevicePolicy } from '@sketchling/core';
import { isNative } from './platform';
import { useApp } from '../state/store';

interface DevicePolicyPlugin {
  getStatus(): Promise<DevicePolicy>;
}

const DevicePolicyNative = registerPlugin<DevicePolicyPlugin>('DevicePolicy');

/** Probe once at launch (and on resume); failures leave the device unmanaged. */
export async function probeDevicePolicy(): Promise<void> {
  if (!isNative) return;
  try {
    const s = await DevicePolicyNative.getStatus();
    useApp.getState().setDevicePolicy({ managed: !!s.managed, supervised: !!s.supervised, purchasesAllowed: s.purchasesAllowed !== false });
  } catch {
    useApp.getState().setDevicePolicy(UNMANAGED_DEVICE);
  }
}

/** Detected policy combined with the dev simulators. */
export function effectivePolicy(): DevicePolicy {
  const { devicePolicy: p, cheats: c } = useApp.getState();
  return {
    managed: p.managed || c.simManaged,
    supervised: p.supervised || c.simSupervised,
    purchasesAllowed: p.purchasesAllowed && !c.simPurchasesOff,
  };
}

/** Reactive gate for screens. */
export function useDeviceGate(): DeviceGate {
  const p = useApp((s) => s.devicePolicy);
  const managed = useApp((s) => s.cheats.simManaged);
  const supervised = useApp((s) => s.cheats.simSupervised);
  const off = useApp((s) => s.cheats.simPurchasesOff);
  const block = useApp((s) => s.config.blockManagedDevices);
  return deviceGate(
    { managed: p.managed || managed, supervised: p.supervised || supervised, purchasesAllowed: p.purchasesAllowed && !off },
    { blockManagedDevices: block !== false },
  );
}
